import React, { useEffect, useState } from "react";

import ProjectList from "./ProjectList";

import { useAppSelector } from "../../redux/hooks";
import { getAuthToken } from "../../redux/reducers/auth";

import {
  parseTermSearchQuery,
  sendEventTracking,
  useQuery,
} from "../../utils/GeneralUtils";
import { useMetaData } from "../../utils/useMetaData";
import { useGetProject, useGetRecommendProjects } from "../../utils/useProject";
import { useLocation, useNavigate } from "react-router-dom";
import { TermType, useGetTerm } from "../../utils/useTerm";
import {
  checkParamExist,
  getCurrentPage,
  getCurrentPerView,
  getParam,
  getParamAll,
  handlePerView,
  searchClearHandler,
  searchHandler,
  SearchParam,
} from "../../common/archive/ListSearchFunc";

const ProjectListContainer: React.FC = () => {
  const auth_token = useAppSelector(getAuthToken);

  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    termData: { entries: games },
    getTermLoading: isGameLoading,
  } = useGetTerm(TermType.Games, undefined, -1);
  const {
    termData: { entries: projectTypes },
    getTermLoading: isTypeLoading,
  } = useGetTerm(TermType.ProjectType, undefined, -1);

  // ターム関連のパラメーターが存在する場合、コンテンツの取得を一時停止します
  const [enabled, setEnabled] = useState<boolean>(
    !checkParamExist(query, [TermType.Games, TermType.ProjectType])
  );

  useEffect(() => {
    if (enabled === true) return;

    if (!isGameLoading && !isTypeLoading) {
      setEnabled(true);
    }
  }, [isGameLoading, isTypeLoading]);

  let selectedGames: { ids: Array<string>; names: Array<string> } = {
    ids: [],
    names: [],
  };
  let selectedProjects: { ids: Array<string>; names: Array<string> } = {
    ids: [],
    names: [],
  };

  if (!isGameLoading && !isTypeLoading) {
    const gameParams = getParamAll(query, TermType.Games);
    selectedGames = {
      ids: parseTermSearchQuery(gameParams, games, "id"),
      names: gameParams,
    };

    const typeParams = getParamAll(query, "projects");
    selectedProjects = {
      ids: parseTermSearchQuery(typeParams, projectTypes, "slug"),
      names: typeParams,
    };
  }

  const categoryData: { value?: string; isFeatured: boolean } = {
    value: getParam(query, "category"),
    isFeatured: false,
  };
  if (categoryData.value === "featured") {
    categoryData.isFeatured = true;
  }

  const sort = getParam(query, "sort") ?? "";
  const pageType = getParam(query, "page_type") ?? "";

  const searchArgs = {
    auth_token: auth_token,
    sort_by: sort !== "" ? sort : "_id",
    games: selectedGames.ids.length > 0 ? selectedGames.ids : undefined,
    category:
      selectedProjects.ids.length > 0 ? selectedProjects.ids : undefined,
    desc: sort === "end_date" ? (pageType === "finished" ? true : false) : true,
    per: getCurrentPerView(query),
    page: getCurrentPage(query),
    search: getParam(query, "s"),
    completed_only: pageType === "finished" ? true : false,
  };

  const {
    items: { projects, total_entries: entries, total_pages: totalPage },
    isLoading: projectLoading,
  } = useGetProject(searchArgs, enabled && !categoryData.isFeatured);

  const { projects: featured, isLoading: featuredLoading } =
    useGetRecommendProjects(categoryData.isFeatured);

  useMetaData({
    title: "eスポーツプロジェクト一覧",
    description:
      "eスポーツやゲームに関する求人・募集が掲載されています。プロジェクトへの応募や過去に募集していたプロジェクトの閲覧、キーワード・カテゴリでの検索をご利用いただけます。",
    imgUrl: "",
  });

  return (
    <ProjectList
      handleListTop={() => {
        navigate("/project", { replace: true });
        sendEventTracking("click", "projectList", "back");
      }}
      projects={projects}
      featured={featured}
      totalEntries={categoryData.isFeatured ? featured.length : entries}
      totalPage={categoryData.isFeatured ? 1 : totalPage}
      currentPage={searchArgs.page}
      isLoading={categoryData.isFeatured ? featuredLoading : projectLoading}
      category={categoryData.value}
      perView={searchArgs.per}
      keywordParam={searchArgs.search ?? ""}
      sort={sort}
      isNormalArchive={pageType === "finished" ? false : true}
      handleArchiveType={(isNormalArchive: boolean) => {
        searchHandler(
          query,
          [{ field: "page_type", value: isNormalArchive ? "" : "finished" }],
          navigate
        );
      }}
      handlePerView={(value: number) =>
        handlePerView(query, location, navigate, value)
      }
      handleSort={(sort: string) =>
        searchHandler(
          query,
          [{ field: "sort", value: sort === "_id" ? "" : sort }],
          navigate
        )
      }
      searchHandler={(params: Array<SearchParam>) => {
        searchHandler(query, params, navigate);
      }}
      clearSearch={() => searchClearHandler(navigate)}
      typeParams={selectedProjects.names}
      gameParams={selectedGames.names}
      typeOptions={projectTypes}
      gameOptions={games}
    />
  );
};

export default ProjectListContainer;
